













































































































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import { ID } from '@/shared/utils/id'
import { Prop, Watch } from 'vue-property-decorator'
import { Editor, EditorContent, EditorFloatingMenu, EditorMenuBar, EditorMenuBubble } from 'tiptap'
import {
  Blockquote,
  Bold,
  BulletList,
  CodeBlock,
  HardBreak,
  Heading,
  History,
  HorizontalRule,
  Italic,
  ListItem,
  OrderedList,
  Placeholder,
  Strike,
  TodoItem,
  TodoList,
  TrailingNode,
  Underline,
} from 'tiptap-extensions'
import { Hooks } from '@/shared/interfaces/hooks'
import { Injector } from '@/config/dependency-injection'
import { DialogService } from '@/shared/dialog/dialog-service'
import ResourceDialog from '@/shared/components/resource-dialog/resource-dialog.vue'
import { Command } from 'tiptap-commands'
import { imageUrl } from '@/shared/utils/images'
import Iframe from '@/shared/components/tiptap-extensions/iframe'
import { FormGroup } from '@/shared/forms/form-group'
import { FigureCaption } from '@/shared/components/tiptap-extensions/figure-caption'
import { Figure } from '@/shared/components/tiptap-extensions/figure'
import Image from '@/shared/components/tiptap-extensions/image'
import Video from '@/shared/components/tiptap-extensions/video'
import { ResourceDialogInput } from '@/shared/components/resource-dialog/resource-dialog-input'
import { ResourceDialogOutput } from '@/shared/components/resource-dialog/resource-dialog-output'
import { videoUrl } from '@/shared/utils/video-url'
import { Source } from '@/shared/components/tiptap-extensions/source'
import { activeUserIsPremium } from '@/shared/utils/user'
import { getVideoId } from '@/shared/utils/get-video-id'
import { NotificationService } from '@/shared/services/notification-service'
import { isNotBlank } from '@/shared/utils/is-blank'
import { Link } from '@/shared/components/tiptap-extensions/link'
import { Focusable } from '@/shared/interfaces/focusable'

@Component({
  components: {
    ResourceDialog,
    EditorFloatingMenu,
    EditorMenuBar,
    EditorContent,
    EditorMenuBubble,
  },
  data() {
    return {
      editor: undefined,
    }
  },
  inject: ['form'],
})
export default class RespondecoEditor extends Vue implements Focusable, Hooks {
  @Prop({
    default: () => false,
    type: Boolean,
  })
  public readonly autoFocus!: boolean

  @Prop({
    type: String,
  })
  public readonly control!: string

  @Prop({
    default: 'advanced',
    type: String,
  })
  public readonly mode!: 'advanced' | 'simple'

  @Prop({
    type: String,
  })
  public readonly placeholder?: string

  @Prop({
    type: String,
  })
  public readonly value?: string

  public readonly form!: FormGroup<any>

  public editor?: Editor
  public id: string = ID()
  public isFullscreen = false

  private ignoreModelUpdate = false

  private dialogService = Injector.get(DialogService)

  get formControl() {
    return this.form.field(this.control)
  }

  get isAdvanced() {
    return this.mode === 'advanced'
  }

  get isInvalid() {
    return this.formControl.$dirty === true && this.formControl.$invalid === true
  }

  get isPremium() {
    return activeUserIsPremium()
  }

  get isSimple() {
    return this.mode === 'simple'
  }

  get showEditorBar(): boolean {
    return this.mode !== 'simple'
  }

  public beforeDestroy() {
    if (this.editor !== undefined) {
      this.editor.destroy()
    }
  }

  public mounted() {
    this.createEditor(this.formControl.$model || this.value || '')

    this.$watch(
      () => this.formControl.$model,
      n => {
        if (this.editor === undefined) {
          this.createEditor(n)
        } else {
          if (n !== this.editor.getHTML()) {
            this.editor.setContent(n)
          }
        }
      }
    )
  }

  public align(alignment: 'left' | 'center' | 'right'): void {
    // noinspection TypeScriptValidateJSTypes
    this.editor?.commands.figure({ type: 'align', value: alignment })
  }

  public focus(): void {
    this.editor?.focus()
  }

  public showLinkAlert(command: Command, url?: string) {
    if (isNotBlank(url)) {
      command({
        href: undefined,
      })
    } else {
      const link = window.prompt('Link', url)

      command({
        href: link,
      })
    }
  }

  public showResourceChooser(command: Command, allowedType: 'image' | 'video' = 'image') {
    this.dialogService
      .openDynamic<ResourceDialogInput, ResourceDialogOutput>(ResourceDialog, {
        allowedType,
      })
      .submitted(resourceVariant => {
        if (allowedType === 'image') {
          command({
            caption: resourceVariant.description,
            src: imageUrl(resourceVariant.location),
            type: 'insert',
          })
        } else {
          command({
            src: videoUrl(resourceVariant.location),
            type: 'video/mp4',
          })
        }
      })
  }

  public showVideoChooser(command: Command) {
    const src = window.prompt('Video URL (Vimeo/ YouTube)') ?? ''

    const videoId = getVideoId(src)

    if (videoId !== false) {
      if (videoId.service === 'youtube') {
        command({
          src: 'https://www.youtube.com/embed/' + videoId.id,
        })
      } else if (videoId.service === 'vimeo') {
        command({
          src: 'https://player.vimeo.com/video/' + videoId.id,
        })
      } else {
        Injector.get(NotificationService).showInfo('common.error.unsupportedVideoService')
      }
    } else {
      Injector.get(NotificationService).showError('common.error.unknownVideoSource')
    }
  }

  public toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen

    document.body.classList.toggle('hide-overflow', this.isFullscreen)
  }

  @Watch('value')
  public onValueChanged(value: string) {
    if (this.ignoreModelUpdate === false) {
      this.editor?.setContent(value)
    }
  }

  private createEditor(content: string) {
    this.editor?.destroy()

    this.editor = new Editor({
      autoFocus: this.autoFocus,
      content,
      extensions: this.getExtensions(),
      onUpdate: ({ getHTML }: Editor) => {
        let html = getHTML()

        // Set the value to empty if the editor is really empty
        // noinspection CheckTagEmptyBody
        if (html === '<p></p>') {
          html = ''
        }

        this.$emit('input', html)

        this.ignoreModelUpdate = true
        this.formControl.$model = html

        this.$nextTick(() => {
          this.ignoreModelUpdate = false
        })
      },
    })
  }

  private getExtensions(): any[] {
    let extensions: any[]

    if (this.mode === 'advanced') {
      extensions = [
        new Blockquote(),
        new BulletList(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [2, 3] }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link({
          openOnClick: false,
        }),
        new Bold(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Image(),
        new Iframe(),
        new Figure(),
        new FigureCaption(),
        new TrailingNode({
          node: 'paragraph',
          notAfter: ['paragraph'],
        }),
        new Source(),
        new Video(),
      ]
    } else {
      extensions = [
        new Blockquote(),
        new BulletList(),
        new ListItem(),
        new OrderedList(),
        new HardBreak(),
        new Bold(),
        new Image(),
        new Figure(),
        new FigureCaption(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Iframe(),
        new Video(),
        new Source(),

        new TrailingNode({
          node: 'paragraph',
          notAfter: ['paragraph'],
        }),

        new Link({
          openOnClick: false,
        }),
      ]
    }

    extensions.forEach(extension => {
      extension.inputRules = () => []
    })

    if (this.placeholder !== undefined) {
      extensions.push(
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          emptyNodeText: this.placeholder,
          showOnlyWhenEditable: true,
          showOnlyCurrent: true,
        })
      )
    }

    return extensions
  }
}
